import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import './Pricing.css';

const PRICE_IDS = {
  monthly: process.env.REACT_APP_STRIPE_PRICE_MONTHLY || 'price_monthly_placeholder',
  annual: process.env.REACT_APP_STRIPE_PRICE_ANNUAL || 'price_annual_placeholder',
};

function Pricing() {
  const [isAnnual, setIsAnnual] = useState(false);
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();
  const { isPro } = useSubscription();
  const navigate = useNavigate();

  const handleSubscribe = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const functions = getFunctions(undefined, 'australia-southeast1');
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
      const result = await createCheckoutSession({
        priceId: isAnnual ? PRICE_IDS.annual : PRICE_IDS.monthly,
      });
      window.location.href = result.data.url;
    } catch (error) {
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pricing-page">
      <div className="pricing-header">
        <h1>Simple, Transparent Pricing</h1>
        <p>Choose the plan that works for you</p>

        <div className="pricing-toggle">
          <span className={!isAnnual ? 'active' : ''}>Monthly</span>
          <button
            className={`toggle-switch ${isAnnual ? 'on' : ''}`}
            onClick={() => setIsAnnual(!isAnnual)}
            aria-label="Toggle annual billing"
          >
            <span className="toggle-knob" />
          </button>
          <span className={isAnnual ? 'active' : ''}>
            Annual <span className="pricing-save-badge">Save 17%</span>
          </span>
        </div>
      </div>

      <div className="pricing-cards">
        {/* Free Tier */}
        <div className="pricing-card">
          <div className="pricing-card-header">
            <h2>Free</h2>
            <div className="pricing-price">
              <span className="pricing-amount">$0</span>
              <span className="pricing-period">/month</span>
            </div>
            <p className="pricing-subtitle">Get started with the basics</p>
          </div>
          <ul className="pricing-features">
            <li><span className="feature-icon">✓</span> Up to 2 clients</li>
            <li><span className="feature-icon">✓</span> 10 shifts per month</li>
            <li><span className="feature-icon">✓</span> Basic invoicing</li>
            <li><span className="feature-icon">✓</span> NDIS Price Guide</li>
            <li className="feature-disabled"><span className="feature-icon">✗</span> Unlimited clients</li>
            <li className="feature-disabled"><span className="feature-icon">✗</span> Unlimited shifts</li>
            <li className="feature-disabled"><span className="feature-icon">✗</span> Service agreements</li>
            <li className="feature-disabled"><span className="feature-icon">✗</span> Auto-invoicing</li>
            <li className="feature-disabled"><span className="feature-icon">✗</span> KM tracking</li>
          </ul>
          <button className="btn btn-secondary pricing-btn" disabled>
            Current Plan
          </button>
        </div>

        {/* Pro Tier */}
        <div className="pricing-card featured">
          <div className="pricing-card-badge">Most Popular</div>
          <div className="pricing-card-header">
            <h2>Pro</h2>
            <div className="pricing-price">
              <span className="pricing-amount">
                ${isAnnual ? '16.58' : '19.99'}
              </span>
              <span className="pricing-period">/month</span>
            </div>
            {isAnnual && (
              <p className="pricing-billed">Billed $199/year</p>
            )}
            <p className="pricing-subtitle">Everything you need to grow</p>
          </div>
          <ul className="pricing-features">
            <li><span className="feature-icon">✓</span> Unlimited clients</li>
            <li><span className="feature-icon">✓</span> Unlimited shifts</li>
            <li><span className="feature-icon">✓</span> Unlimited invoices</li>
            <li><span className="feature-icon">✓</span> Service agreements</li>
            <li><span className="feature-icon">✓</span> NDIS Price Guide</li>
            <li><span className="feature-icon">✓</span> Auto-invoicing</li>
            <li><span className="feature-icon">✓</span> KM tracking</li>
            <li><span className="feature-icon">✓</span> All future features</li>
          </ul>
          {isPro ? (
            <button className="btn btn-primary pricing-btn" disabled>
              ✓ You're on Pro
            </button>
          ) : (
            <button
              className="btn btn-primary pricing-btn"
              onClick={handleSubscribe}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Subscribe Now'}
            </button>
          )}
          <p className="pricing-promo-note">
            Have a promo code? Enter it at checkout
          </p>
        </div>
      </div>
    </div>
  );
}

export default Pricing;
